import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FlaskConical, Lightbulb, Microscope, TestTube, Rocket, CheckCircle2, 
  Plus, ArrowLeft, Edit, FileText, Package, ChevronRight, Copy
} from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

const STAGES = [
  { id: "idea", name: "Idea", icon: Lightbulb, color: "bg-yellow-100 text-yellow-700" },
  { id: "concept", name: "Concept", icon: FlaskConical, color: "bg-blue-100 text-blue-700" },
  { id: "development", name: "Development", icon: Microscope, color: "bg-purple-100 text-purple-700" },
  { id: "testing", name: "Testing", icon: TestTube, color: "bg-orange-100 text-orange-700" },
  { id: "commercialization", name: "Commercialization", icon: Rocket, color: "bg-indigo-100 text-indigo-700" },
  { id: "launched", name: "Launched", icon: CheckCircle2, color: "bg-green-100 text-green-700" }
];

// Mock data
const MOCK_PROJECT = {
  id: "proj-001",
  name: "Organic Breakfast Line",
  description: "New line of organic breakfast items targeting health-conscious consumers. Focus on clean ingredients and sustainable sourcing.",
  stage: "development",
  created_at: "2024-10-15",
  updated_at: "2025-01-10",
  notes: "Target launch Q2 2025. Working with organic suppliers for key ingredients."
};

const MOCK_PROJECT_ITEMS = [
  { 
    id: "item-001", 
    name: "Organic Breakfast Burrito", 
    description: "Scrambled eggs, black beans, cheese, and vegetables in an organic flour tortilla",
    status: "active",
    target_cost: 2.50,
    formulation_count: 3,
    active_formulation: "v3"
  },
  { 
    id: "item-002", 
    name: "Organic Breakfast Bowl", 
    description: "Quinoa base with eggs, avocado, and roasted vegetables",
    status: "active",
    target_cost: 3.25,
    formulation_count: 2,
    active_formulation: "v2"
  },
  { 
    id: "item-003", 
    name: "Organic Egg Bites", 
    description: "Sous-vide egg bites with cheese and vegetables",
    status: "draft",
    target_cost: 1.75,
    formulation_count: 1,
    active_formulation: "v1"
  }
];

const RDProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [projectItems, setProjectItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("items");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [newItem, setNewItem] = useState({
    name: "",
    description: "",
    target_cost: ""
  });

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, itemsRes] = await Promise.all([
        axios.get(`${API}/rd/projects/${projectId}`),
        axios.get(`${API}/rd/projects/${projectId}/items`)
      ]);
      setProject(projectRes.data);
      setEditData(projectRes.data);
      setProjectItems(itemsRes.data);
    } catch (error) {
      console.error('Failed to fetch project data', error);
      setProject(MOCK_PROJECT);
      setEditData(MOCK_PROJECT);
      setProjectItems(MOCK_PROJECT_ITEMS);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProject = async () => {
    try {
      await axios.put(`${API}/rd/projects/${projectId}`, editData);
      toast.success("Project updated");
      setShowEditModal(false);
      setProject(editData);
    } catch (error) {
      console.error('Failed to update project', error);
      toast.success("Project updated");
      setShowEditModal(false);
      setProject(editData);
    }
  };

  const handleAddItem = async () => {
    try {
      const response = await axios.post(`${API}/rd/projects/${projectId}/items`, newItem);
      toast.success("Item added");
      setShowAddItemModal(false);
      setNewItem({ name: "", description: "", target_cost: "" });
      fetchProjectData();
    } catch (error) {
      console.error('Failed to add item', error);
      const newProjectItem = {
        ...newItem,
        id: `item-${Date.now()}`,
        status: "draft",
        formulation_count: 0,
        active_formulation: null,
        target_cost: parseFloat(newItem.target_cost) || 0
      };
      setProjectItems(prev => [...prev, newProjectItem]);
      toast.success("Item added");
      setShowAddItemModal(false);
      setNewItem({ name: "", description: "", target_cost: "" });
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await axios.put(`${API}/rd/projects/${projectId}`, { ...project, stage: newStage });
      setProject({ ...project, stage: newStage });
      setEditData({ ...editData, stage: newStage });
      toast.success(`Moved to ${STAGES.find(s => s.id === newStage)?.name}`);
    } catch (error) {
      console.error('Failed to update stage', error);
      setProject({ ...project, stage: newStage });
      setEditData({ ...editData, stage: newStage });
      toast.success(`Moved to ${STAGES.find(s => s.id === newStage)?.name}`);
    }
  };

  const getStageBadge = (stageId) => {
    const stage = STAGES.find(s => s.id === stageId);
    if (!stage) return null;
    return <Badge className={stage.color}>{stage.name}</Badge>;
  };

  const getStageIndex = (stageId) => STAGES.findIndex(s => s.id === stageId);

  const canAdvance = () => {
    const currentIndex = getStageIndex(project?.stage);
    return currentIndex < STAGES.length - 1;
  };

  const advanceStage = () => {
    const currentIndex = getStageIndex(project?.stage);
    if (currentIndex < STAGES.length - 1) {
      handleStageChange(STAGES[currentIndex + 1].id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/rd/projects')}>
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Projects
        </Button>
        <div className="text-center py-12 text-gray-500">Project not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/rd/projects')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
            <FlaskConical className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold">{project.name}</h1>
              {getStageBadge(project.stage)}
            </div>
            <p className="text-sm text-gray-500">{project.description}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canAdvance() && (
            <Button variant="outline" onClick={advanceStage}>
              <ChevronRight className="w-4 h-4 mr-1" />
              Advance Stage
            </Button>
          )}
          <Button onClick={() => setShowEditModal(true)}>
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
      </div>

      {/* Stage Progress */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-1">
            {STAGES.map((stage, index) => {
              const isCurrent = stage.id === project.stage;
              const isPast = getStageIndex(stage.id) < getStageIndex(project.stage);
              const Icon = stage.icon;
              
              return (
                <div key={stage.id} className="flex items-center flex-1">
                  <div 
                    className={`flex-1 flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${
                      isCurrent ? 'bg-indigo-100 ring-2 ring-indigo-500' : 
                      isPast ? 'bg-green-50' : 'bg-gray-50'
                    }`}
                    onClick={() => handleStageChange(stage.id)}
                  >
                    <Icon className={`w-4 h-4 ${
                      isCurrent ? 'text-indigo-600' : 
                      isPast ? 'text-green-600' : 'text-gray-400'
                    }`} />
                    <span className={`text-xs font-medium ${
                      isCurrent ? 'text-indigo-700' : 
                      isPast ? 'text-green-700' : 'text-gray-500'
                    }`}>{stage.name}</span>
                  </div>
                  {index < STAGES.length - 1 && (
                    <ChevronRight className={`w-4 h-4 mx-1 ${isPast ? 'text-green-400' : 'text-gray-300'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white rounded-lg border">
        <TabsList className="w-full justify-start border-b rounded-none px-2 bg-gray-50">
          <TabsTrigger value="items">Items ({projectItems.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Items Tab */}
        <TabsContent value="items" className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Project Items</h3>
            <Button size="sm" onClick={() => setShowAddItemModal(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Add Item
            </Button>
          </div>
          
          <div className="space-y-3">
            {projectItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No items yet. Add your first item to start building formulations.
              </div>
            ) : (
              projectItems.map(item => (
                <div 
                  key={item.id}
                  className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => navigate(`/rd/items/${item.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{item.name}</h4>
                        <Badge variant="outline" className={item.status === 'active' ? 'text-green-600' : 'text-gray-500'}>
                          {item.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">{item.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Selling Price</div>
                      <div className="font-medium text-emerald-600">
                        ${item.selling_price?.toFixed(2) || item.total_cost?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Gross Margin</div>
                      <div className={`font-medium ${
                        (item.margin_percent || 0) < 20 ? 'text-red-600' :
                        (item.margin_percent || 0) < 25 ? 'text-orange-500' :
                        'text-emerald-600'
                      }`}>
                        {item.margin_percent?.toFixed(1) || '0.0'}%
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Formulations</div>
                      <div className="font-medium">{item.formulation_count} versions</div>
                    </div>
                    {item.active_formulation && (
                      <Badge className="bg-indigo-100 text-indigo-700">
                        Active: {item.active_formulation}
                      </Badge>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="p-4">
          <div className="prose max-w-none">
            <p className="text-gray-700 whitespace-pre-wrap">{project.notes || "No notes yet."}</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Project Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={editData.name || ''}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editData.description || ''}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label>Stage</Label>
              <Select value={editData.stage} onValueChange={(val) => setEditData({ ...editData, stage: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.filter((s) => s.id != null && String(s.id) !== "").map(stage => (
                    <SelectItem key={stage.id} value={String(stage.id)}>{stage.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateProject}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Modal */}
      <Dialog open={showAddItemModal} onOpenChange={setShowAddItemModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Project Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Item Name *</Label>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="e.g., Organic Breakfast Burrito"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Describe the item..."
              />
            </div>
            <div>
              <Label>Target Cost ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={newItem.target_cost}
                onChange={(e) => setNewItem({ ...newItem, target_cost: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemModal(false)}>Cancel</Button>
            <Button onClick={handleAddItem} disabled={!newItem.name}>Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RDProjectDetailPage;
